import {
  Component,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, forkJoin } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { ContactService } from '../core/services/contact.service';
import { AddressService } from '../core/services/address.service';
import { UserRoleService } from '../services/user-role.service';
import {
  ContactMailPhoneService,
  ContactMailPhoneCreate,
  ContactMailPhoneUpdate,
} from '../core/services/contact-mailphone.service';
import { TranslatePipe } from '../core/features/translations';
import {
  ContactEditDialogComponent,
  ContactEditDialogData,
  ContactEditDialogResult,
} from '../my-data/dialogs/contact-edit-dialog.component';
import { CountryService } from '../core/services/country.service';
import { ContactUpdate, AddressUpdate } from '../core/models';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './dashboard.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dashboard implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly contactService = inject(ContactService);
  private readonly addressService = inject(AddressService);
  private readonly contactMailPhoneService = inject(ContactMailPhoneService);
  private readonly dialog = inject(MatDialog);
  private readonly countryService = inject(CountryService);
  private readonly userRoleService = inject(UserRoleService);

  userInfo = signal<any>(null);
  backendUserInfo = signal<any>(null);
  contactData = signal<any>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly countries = this.countryService.countries;

  // Store address and mailphone IDs for updates
  private readonly addressId = signal<number | null>(null);
  private readonly phoneMailPhoneId = signal<number | null>(null);
  private readonly phoneData = signal<string>('');
  private readonly phoneToCheck = signal<string | null>(null);
  private readonly addressToCheck = signal<string | null>(null);

  // Computed signal to check if phone number or address data needs to be checked
  // Based on ToCheck field (date set by nightly script when data is missing)
  readonly isMissingPhoneNumber = computed(() => {
    const phoneToCheckDate = this.phoneToCheck();
    return phoneToCheckDate !== null && phoneToCheckDate !== undefined;
  });

  readonly isMissingAddressData = computed(() => {
    const addressToCheckDate = this.addressToCheck();
    return addressToCheckDate !== null && addressToCheckDate !== undefined;
  });

  // Combined signal for any missing data (phone or address)
  readonly isMissingData = computed(() => {
    return this.isMissingPhoneNumber() || this.isMissingAddressData();
  });

  // Greeting based on time of day
  readonly greetingKey = computed(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'portal.dashboard.greeting.morning';
    if (hour < 18) return 'portal.dashboard.greeting.afternoon';
    return 'portal.dashboard.greeting.evening';
  });

  // Troubleshooting data for support
  readonly troubleshootingData = computed(() => {
    const contact = this.contactData();
    return {
      contact_id: contact?.contact_id || '-',
      first_name: contact?.prename || '-',
      last_name: contact?.lastname || '-',
      isStudent: this.userRoleService.isStudent(),
      isLecturer: this.userRoleService.isLecturer(),
    };
  });

  // User first name for personalization
  readonly userFirstName = computed(() => {
    const contact = this.contactData();
    return contact?.prename || '';
  });

  ngOnInit(): void {
    // Get user info from localStorage (from ID token)
    const info = this.authService.getUserInfo();
    this.userInfo.set(info);

    // Load countries
    this.countryService.getCountries().subscribe();

    // TEST: Fetch user info from backend (triggers auth interceptor + token validation)
    this.userService.getCurrentUser().subscribe({
      next: (data) => {
        this.backendUserInfo.set(data);
      },
      error: (err) => {
        console.error('❌ Backend request failed:', err);
        console.error('Status:', err.status);
        console.error('Message:', err.message);
      },
    });

    // Load contact data to check for missing phone number
    this.loadContactData();
  }

  /**
   * Load contact data with phone number and check ToCheck fields
   */
  private loadContactData(): void {
    const userInfo = this.authService.getUserInfo();
    if (!userInfo) return;

    const userEmail = userInfo.sub || userInfo.email;
    if (!userEmail) return;

    this.loading.set(true);

    this.contactService
      .getContactByEmail(userEmail)
      .pipe(
        switchMap((contact) => {
          return this.contactService.getContactComplete(contact.contact_id);
        })
      )
      .subscribe({
        next: (completeData: any) => {
          const contact = completeData.contact;
          const addresses = completeData.addresses || [];
          const mailphones = completeData.mailphones || [];

          // Find primary address
          const primaryAddress = addresses.find((a: any) => a.is_primary_address) || addresses[0];

          // Store address_id and ToCheck for updates
          if (primaryAddress?.fk_address_id) {
            this.addressId.set(primaryAddress.fk_address_id);
          }
          if (primaryAddress?.ToCheck) {
            this.addressToCheck.set(primaryAddress.ToCheck);
          } else {
            this.addressToCheck.set(null);
          }

          // Find phone number
          const phoneEntry =
            mailphones.find(
              (m: any) =>
                m.contact_mailphone_data && !m.contact_mailphone_data.includes('@') && m.is_primary
            ) ||
            mailphones.find(
              (m: any) => m.contact_mailphone_data && !m.contact_mailphone_data.includes('@')
            );

          // Store phone mailphone_id, data, and ToCheck for updates
          if (phoneEntry) {
            this.phoneMailPhoneId.set(phoneEntry.contact_mailphone_id || null);
            this.phoneData.set(phoneEntry.contact_mailphone_data || '');
            if (phoneEntry.ToCheck) {
              this.phoneToCheck.set(phoneEntry.ToCheck);
            } else {
              this.phoneToCheck.set(null);
            }
          } else {
            this.phoneMailPhoneId.set(null);
            this.phoneData.set('');
            this.phoneToCheck.set(null);
          }

          // Build contact object with phone and address data
          const enrichedContact = {
            ...contact,
            prename: primaryAddress?.prename || contact.prename || '',
            lastname: primaryAddress?.lastname || contact.lastname || '',
            companyname: primaryAddress?.companyname || contact.companyname || '',
            phone: phoneEntry?.contact_mailphone_data || '',
          };

          this.contactData.set(enrichedContact);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          console.error('❌ Error loading contact data:', err);
          this.loading.set(false);
        },
      });
  }

  /**
   * Open edit dialog for contact data
   */
  openEditDialog(): void {
    const contact = this.contactData();
    if (!contact) return;

    const dialogData: ContactEditDialogData = {
      contact: contact,
      countries: this.countries(),
    };

    const dialogRef = this.dialog.open(ContactEditDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'large-dialog',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: ContactEditDialogResult | undefined) => {
      if (result) {
        this.saveContactFromDialog(result.formValue);
      }
    });
  }

  /**
   * Save contact data from dialog
   */
  private saveContactFromDialog(formValue: any): void {
    const contact = this.contactData();
    if (!contact) {
      console.error('❌ No contact loaded');
      return;
    }

    this.saving.set(true);

    // Prepare contact update (only contact table fields)
    // ✅ Include all fields, even if empty (allows clearing values)
    const contactUpdateData: ContactUpdate = {
      gender: formValue.gender || null,
      birthday: formValue.birthday || null,
      nationality: formValue.nationality || null,
      hometown: formValue.hometown || null,
      ahv: formValue.ahv_number || null,
    };

    // Prepare address update for primary address (name fields)
    // ✅ Include all fields, even if empty
    const addressUpdateData: AddressUpdate = {
      prename: formValue.prename || null,
      lastname: formValue.lastname || null,
      companyname: formValue.companyname || null,
    };

    // Update contact, address, and phone
    const updates = [];

    // ✅ Always update contact (to ensure fields can be cleared)
    updates.push(this.contactService.updateContact(contact.contact_id, contactUpdateData));

    // ✅ Always update primary address (to ensure name fields can be cleared)
    const addrId = this.addressId();
    if (addrId) {
      updates.push(this.addressService.updateAddress(addrId, addressUpdateData));
    }

    // Handle phone number update/create/delete
    const newPhone = formValue.phone?.trim() || '';
    const oldPhone = this.phoneData();
    const phoneMailPhoneId = this.phoneMailPhoneId();

    if (newPhone !== oldPhone) {
      if (newPhone && phoneMailPhoneId) {
        // Update existing phone number
        updates.push(
          this.contactMailPhoneService.updateContactMailPhone(phoneMailPhoneId, {
            contact_mailphone_data: newPhone,
          })
        );
      } else if (newPhone && !phoneMailPhoneId) {
        // Create new phone number entry (category_id 3 = Mobiltelefon)
        updates.push(
          this.contactMailPhoneService.createContactMailPhone({
            fk_contact_id: contact.contact_id,
            fk_category_id: 3,
            contact_mailphone_data: newPhone,
            is_primary: true,
          })
        );
      } else if (!newPhone && phoneMailPhoneId) {
        // Delete phone number (empty string)
        updates.push(this.contactMailPhoneService.deleteContactMailPhone(phoneMailPhoneId));
      }
    }

    // ✅ Execute all updates (contact, address, phone if changed)
    forkJoin(updates).subscribe({
      next: () => {
        // After successful save, clear ToCheck flags
        this.contactService.clearToCheckFlags(contact.contact_id, 'all').subscribe({
          next: () => {
            this.saving.set(false);
            // Reload contact data to reflect changes
            this.loadContactData();
          },
          error: (err: unknown) => {
            // Even if clearing ToCheck fails, consider the save successful
            console.warn('⚠️ Warning: Failed to clear ToCheck flags:', err);
            this.saving.set(false);
            // Reload contact data to reflect changes
            this.loadContactData();
          },
        });
      },
      error: (err: unknown) => {
        this.saving.set(false);
        console.error('❌ Error saving data:', err);
      },
    });
  }
}
